import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../Apis/Tables";
import { getFullName } from "../../../Apis/functions";
import TableFunction from "../../../components/table/TableFunction";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { VIEW_TYPE } from "../../../enums/ViewType";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * White list logic of the building detail: paginated search of the residents
 * authorized in the building, rendered as a read only card grid.
 *
 * @param {Object} props
 * @param {string|number} props.buildingId - Building the white list belongs to.
 * @param {number} props.refresh - Counter bumped by the add modal to reload the list.
 * @param {Function} props.onTotalChange - Reports the total of the list to the badge of the tab.
 * @returns {Object} Localization function, list state and the search handlers.
 */
export const ResidentsWhiteListApp = ({ buildingId, refresh, onTotalChange }) => {
    const dispatch = useDispatch();
    const option = TABLES.BUILDING_WHITE_LIST;
    const [searchElement, setSearchElement] = useState("");
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [modified, setModified] = useState(false);
    // Flags SearchSimple works with, the search itself runs through getData
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [bodyForExport, setBodyForExport] = useState('');
    // Building the last fetch ran with, the detail is not loaded yet on mount
    const requestedBuildingId = useRef(null);
    // Scrollable container of the grid, panelTables-container sizes it with --top-value
    const elementRef = useRef(null);

    /**
     * Loads the white list page the grid renders, the entries of the building
     * are searched through the BUILDING_USER model of the simple search by id,
     * which takes the building as the userId of the body.
     *
     * @param {number} page - Page requested by the pagination, zero based.
     * @param {Object} [options] - Extra values sent by TableFunction.
     * @param {number} [options.rowsPerPage] - Page size in use.
     * @param {string} [options.searchElement] - Search value the fetch runs with.
     */
    const getData = (page, options) => {
        // The building is required by the search, on the first render of the
        // detail it is still loading and the fetch is left to the effect below
        if (!buildingId) return;

        requestedBuildingId.current = buildingId;

        const key = options?.searchElement ?? searchElement;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key,
            userId: buildingId
        });

        setBodyForExport(body);

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                // The badge of the tab paints the whole list, a filtered search
                // is not the total of the white list
                if (!key) onTotalChange?.(data?.totalElements || 0);

                setResults({
                    ...data,
                    // The card paints one name, the service returns it split
                    content: data?.content?.map((resident) => ({
                        ...resident,
                        fullName: resident?.fullName
                            || getFullName(resident?.name, resident?.lastName, resident?.secondLastName)
                    })) || []
                });
            }
        );
    };

    // Use TableFunction to handle the grid logic (pagination)
    const {
        t,
        pagination,
        rowsPerPage
    } = TableFunction({
        data: results,
        labelRowsPerPage: 'users_per_page',
        hasContainer: true,
        fixedPagination: false,
        view: VIEW_TYPE.GRID.name,
        searchElement,
        getData,
        modified,
        setModified
    });

    // Loads the list once the building detail arrives, the mount fetch of
    // TableFunction is skipped while there is no building to search with
    useEffect(() => {
        if (!buildingId || requestedBuildingId.current === buildingId) return;

        setModified(true);
    }, [buildingId]);

    // Reloads the list after the add modal saves new entries
    useEffect(() => {
        if (!buildingId || !refresh) return;

        setModified(true);
    }, [refresh]);

    /**
     * Distance from the top of the viewport to the grid, panelTables-container
     * takes the rest of the screen with it and leaves the pagination outside
     * of the scroll.
     */
    useLayoutEffect(() => {
        if (!elementRef?.current) return;

        const distanceTop = elementRef.current.getBoundingClientRect().top + 90;
        elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
    }, [results?.content]);

    /**
     * Search of SearchSimple, the white list needs the building on the body so
     * the fetch is the same getData of the grid instead of the generic one.
     */
    const handleSearch = () => getData(0);

    /**
     * Reset of SearchSimple, brings the first page back without the search key.
     */
    const handleResetSearch = () => getData(0, { searchElement: "" });

    return {
        t,
        option,
        searchElement,
        setSearchElement,
        results,
        setResults,
        rowsPerPage,
        search,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        handleSearch,
        handleResetSearch,
        pagination,
        elementRef,
    };
};
